<div class="mb-3">
    <h4 class="text-primary mb-0">Message Suppression</h4>
</div>

<div class="alert alert-warning" role="alert">
    <fa-duotone-icon icon="triangle-exclamation" class="me-2" />
    <strong>Caution:</strong> Suppression and recipient overrides change how this organization communicates with its members. Only enable them when you understand the impact.
</div>

@if (errorMessage()) {
    <div class="alert alert-danger" role="alert">
        <fa-duotone-icon icon="circle-exclamation" class="me-2" />
        {{ errorMessage() }}
    </div>
}

@if (!errorMessage() && showActiveBanner()) {
    <div class="alert alert-danger" role="alert">
        @if (currentSuppression()?.suppressMessages) {
            <div>
                <strong>Suppression is active.</strong>
                No emails, SMS, or push notifications will be delivered for this organization.
            </div>
        }
        @if (currentSuppression()?.overrideEmailRecipient) {
            <div [class.mt-2]="currentSuppression()?.suppressMessages">
                <strong>Email recipient override is active.</strong>
                Outbound emails are being redirected to
                <code>{{ currentSuppression()?.overrideEmailAddress }}</code>.
            </div>
        }
    </div>
}

<form [formGroup]="suppressionForm" (ngSubmit)="onSaveClicked()" novalidate>

    <div class="mb-4">
        @if (isLoading()) {
            <div class="placeholder-glow">
                <span class="placeholder col-6 d-block mb-2"></span>
                <span class="placeholder col-8 d-block"></span>
            </div>
        } @else {
            <div class="form-check form-switch fs-5">
                <input type="checkbox"
                    id="suppressMessages"
                    class="form-check-input"
                    role="switch"
                    formControlName="suppressMessages"
                    (change)="onSuppressMessagesToggled()" />
                <label for="suppressMessages" class="form-check-label fw-semibold fs-6">
                    Suppress all messages from this organization
                </label>
            </div>
            <p class="text-muted small mb-0 mt-1">
                When enabled, the messaging service will drop every email, SMS, and push notification queued by this organization.
                Mutually exclusive with email recipient override.
            </p>
        }
    </div>

    <div class="mb-3">
        @if (isLoading()) {
            <div class="placeholder-glow">
                <span class="placeholder col-6 d-block mb-2"></span>
                <span class="placeholder col-8 d-block"></span>
            </div>
        } @else {
            <div class="form-check form-switch fs-5">
                <input type="checkbox"
                    id="overrideEmailRecipient"
                    class="form-check-input"
                    role="switch"
                    formControlName="overrideEmailRecipient"
                    (change)="onOverrideEmailRecipientToggled()" />
                <label for="overrideEmailRecipient" class="form-check-label fw-semibold fs-6">
                    Override email recipient
                </label>
            </div>
            <p class="text-muted small mb-0 mt-1">
                When enabled, every outbound email is redirected to a specified address instead of the intended recipients.
                Mutually exclusive with full suppression.
            </p>
        }
    </div>

    @if (!isLoading() && overrideEnabled()) {
        <div class="mb-4 ms-4">
            <ms-input-label [labelText]="'Override email address'" [isRequired]="true" />
            <input type="email"
                class="form-control"
                formControlName="overrideEmailAddress"
                placeholder="qa-inbox@membershine.com"
                maxlength="254"
                [class.is-invalid]="suppressionForm.get('overrideEmailAddress')?.invalid && suppressionForm.get('overrideEmailAddress')?.touched"
                [class.is-valid]="suppressionForm.get('overrideEmailAddress')?.valid && suppressionForm.get('overrideEmailAddress')?.touched && suppressionForm.get('overrideEmailAddress')?.value !== ''" />
            @if (suppressionForm.get('overrideEmailAddress')?.invalid && suppressionForm.get('overrideEmailAddress')?.touched) {
                <div class="error-messages">
                    @if (suppressionForm.get('overrideEmailAddress')?.errors?.['required']) {
                        <p class="text-danger text-start pt-1 mb-0">{{ FormValidatorMessages.MessageSuppressionOverrideEmailRequired }}</p>
                    }
                    @if (suppressionForm.get('overrideEmailAddress')?.errors?.['email']) {
                        <p class="text-danger text-start pt-1 mb-0">{{ suppressionForm.get('overrideEmailAddress')?.errors?.['email']?.message }}</p>
                    }
                </div>
            }
        </div>
    }

    <div class="d-flex justify-content-end gap-2 mt-4">
        <button type="button"
            class="btn btn-ms-secondary"
            [disabled]="isLoading() || isSubmitting() || !hasFormBeenModified()"
            (click)="onResetClicked()">
            Cancel
        </button>
        <button type="submit"
            class="btn btn-primary"
            [disabled]="isLoading() || isSubmitting() || !hasFormBeenModified() || suppressionForm.invalid">
            @if (isSubmitting()) {
                <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Saving...
            } @else {
                Save changes
            }
        </button>
    </div>
</form>

<ms-confirmation-modal #messageSuppressionConfirmationModalRef
    modalId="messageSuppressionConfirmationModal"
    [isSubmitting]="isSubmitting()"
    (confirmed)="onConfirmationConfirmed()"
    (cancelled)="onConfirmationCancelled()"
/>

<ms-info-modal #messageSuppressionInfoModalRef
    modalId="messageSuppressionInfoModal"
/>
